import React, { useContext, useCallback } from "react";

import { Instant } from "js-joda";

import { DateLiteral } from "@plex/culture-react";
import { SelectionMode, DataTable, DataTablePlugins, StickyHeaderRow, createComparer } from "@plex/react-components";

import { Roles } from "config/security";
import { commonMessages } from "infrastructure/localization";
import { UserContext } from "ui/app/user-provider/UserContext";
import { useLocalizedValues } from "ui/hooks";
import { securityMessages } from "ui/screens/security/security.localization";

import styles from "../SecurityRoleMembers.module.scss";
import { ISecurityRoleMember } from "../SecurityRoleMembers.state";

export interface ISecurityRoleMembersGridProps {
  items?: ISecurityRoleMember[];
  selectedItems: ISecurityRoleMember[];
  onSelectionChanged: (rows: ISecurityRoleMember[]) => void;
}

export const SecurityRoleMembersGrid: React.FC<ISecurityRoleMembersGridProps> = ({ items, onSelectionChanged, selectedItems }) => {
  const localizedValues = useLocalizedValues(() => ({
    refineSearch: commonMessages.info("refineSearch"),
    startSearch: commonMessages.info("startSearch"),
    tenantUsers: securityMessages.property("tenantUsers"),
    tenant: securityMessages.property("tenant"),
    validDateFrom: securityMessages.property("validDateFrom"),
    validDateTo: securityMessages.property("validDateTo"),
    enterpriseWide: securityMessages.property("enterpriseWide"),
    yes: commonMessages.option("yes"),
    no: commonMessages.option("no")
  }));

  const user = useContext(UserContext);

  const canSelectRow = useCallback((row: ISecurityRoleMember) => user.isAuthorized([{ tenants: [row.tenantId], roles: [Roles.SecurityAdmin] }]), [user]);

  return (
    <DataTable<ISecurityRoleMember>
      keySelector={row => row.userId + row.tenantId}
      data={items || []}
      plugins={[
        DataTablePlugins.Selections<ISecurityRoleMember>({
          selected: selectedItems,
          onSelectionChanged,
          mode: SelectionMode.multiple,
          canSelectRow
        }),
        DataTablePlugins.Loader(),
        DataTablePlugins.Sorter<ISecurityRoleMember>()
      ]}
    >
      {!items?.length && <DataTable.Caption captionText={items ? localizedValues.refineSearch : localizedValues.startSearch} />}
      <DataTable.Header>
        <StickyHeaderRow>
          <DataTable.HeaderSelectionCell />
          <DataTable.HeaderCell<ISecurityRoleMember> sortable sortComparer={createComparer(row => row.user)}>
            {localizedValues.tenantUsers}
          </DataTable.HeaderCell>
          <DataTable.HeaderCell<ISecurityRoleMember> sortable sortComparer={createComparer(row => row.validDateFrom)}>
            {localizedValues.validDateFrom}
          </DataTable.HeaderCell>
          <DataTable.HeaderCell<ISecurityRoleMember> sortable sortComparer={createComparer(row => row.validDateTo)}>
            {localizedValues.validDateTo}
          </DataTable.HeaderCell>
          <DataTable.HeaderCell<ISecurityRoleMember> sortable sortComparer={createComparer(row => row.enterpriseWide)} align="center">
            {localizedValues.enterpriseWide}
          </DataTable.HeaderCell>
        </StickyHeaderRow>
      </DataTable.Header>
      <DataTable.GroupBy<ISecurityRoleMember> groupSelector={row => row.tenantName}>
        {group => (
          <>
            <DataTable.GroupHeaderRow className={styles.trTopBorderless}>
              <DataTable.Cell />
              <DataTable.Cell>
                <div className={styles.group}>
                  <div>{group[0].tenantName}</div>
                </div>
              </DataTable.Cell>
              <DataTable.Cell />
              <DataTable.Cell />
              <DataTable.Cell />
            </DataTable.GroupHeaderRow>
            <DataTable.GroupBody<ISecurityRoleMember>>
              {(row, index, rowInfo) => {
                const isSelected = rowInfo?.selectionState.selected;
                const toggleSelection = () => rowInfo?.selectionState.toggle();

                const validDateFrom = row.validDateFrom && Instant.parse(row.validDateFrom);
                const validDateTo = row.validDateTo && Instant.parse(row.validDateTo);

                return (
                  <DataTable.Row key={rowInfo?.key} alt={index != null && index % 2 !== 0} selectable selected={isSelected}>
                    <DataTable.SelectionCell disabled={!canSelectRow(row)} />
                    <DataTable.Cell onClick={toggleSelection}>{row.user}</DataTable.Cell>
                    <DataTable.Cell onClick={toggleSelection}>
                      <DateLiteral value={validDateFrom} options={{ datetime: "medium" }} />
                    </DataTable.Cell>
                    <DataTable.Cell onClick={toggleSelection}>
                      <DateLiteral value={validDateTo} options={{ datetime: "medium" }} />
                    </DataTable.Cell>
                    <DataTable.Cell onClick={toggleSelection} align="center">
                      {row.enterpriseWide ? localizedValues.yes : localizedValues.no}
                    </DataTable.Cell>
                  </DataTable.Row>
                );
              }}
            </DataTable.GroupBody>
          </>
        )}
      </DataTable.GroupBy>
    </DataTable>
  );
};
